/**
 * Runtime bridge between the authoritative replay tick and the Live Analysis
 * UI.  The replay engine publishes immutable snapshots; consumers never get a
 * reference to the physics objects used by playback.
 */

let latestFrame = null;
const listeners = new Set();
const pauseListeners = new Set();

function clone(value) {
  if (value == null) return value;
  if (typeof structuredClone === 'function') {
    try { return structuredClone(value); } catch (_) { /* fall through */ }
  }
  try { return JSON.parse(JSON.stringify(value)); } catch (_) { return value; }
}

export function publishLiveAnalysisFrame(frame) {
  if (!frame || typeof frame !== 'object') return null;
  latestFrame = clone(frame);
  listeners.forEach((listener) => {
    try { listener(clone(latestFrame)); } catch (_) { /* observers are isolated */ }
  });
  return latestFrame;
}

export function getLatestLiveAnalysisFrame() {
  return clone(latestFrame);
}

export function subscribeLiveAnalysis(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  if (latestFrame) {
    try { listener(clone(latestFrame)); } catch (_) { /* ignore observer errors */ }
  }
  return () => listeners.delete(listener);
}

export function subscribeLiveAnalysisPause(listener) {
  if (typeof listener !== 'function') return () => {};
  pauseListeners.add(listener);
  return () => pauseListeners.delete(listener);
}

export function requestLiveAnalysisPause(reason = 'decision') {
  pauseListeners.forEach((listener) => {
    try { listener(reason); } catch (_) { /* ignore observer errors */ }
  });
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(new CustomEvent('haxball-live-analysis-pause', { detail: { reason } }));
    } catch (_) { /* CustomEvent is unavailable in some test environments */ }
  }
  // The native replay controls own playback. Toggle only when the replay is
  // currently playing, preserving user pause state and avoiding a resume race.
  if (typeof document !== 'undefined') {
    try {
      const icon = document.querySelector('[data-hook="playicon"]');
      const play = document.querySelector('[data-hook="play"]');
      if (icon && play && String(icon.textContent || '').trim() === '⏸') play.click();
    } catch (_) { /* DOM controls are absent in unit tests/headless parsing */ }
  }
}

export function clearLiveAnalysisRuntime() {
  latestFrame = null;
  listeners.forEach((listener) => {
    try { listener(null); } catch (_) { /* ignore observer errors */ }
  });
}

export function hasLiveAnalysisSubscribers() {
  return listeners.size > 0;
}
